import type { FormField, KycFieldId } from '@funkit/connect-core'
import type { TextInputProps } from 'react-native'

/**
 * What the platform needs in order to offer an address the user already has.
 *
 * Both are declared: iOS reads `textContentType`, Android reads `autoComplete`,
 * and a field that declares only one is filled on only one platform.
 */
export interface FieldAutofill {
  textContentType?: TextInputProps['textContentType']
  autoComplete?: TextInputProps['autoComplete']
  /** Names are capitalised as the user would write them, codes as codes. */
  autoCapitalize?: TextInputProps['autoCapitalize']
}

/**
 * Autofill hints per field, keyed by the contract's canonical field id.
 *
 * Native autofill, and nothing else: this is the signup case, so the address
 * the user's device already holds is the one they want. No SDK, no API key and
 * no billing account — the platform props are the whole feature.
 *
 * An id this build has not seen declares nothing, which is the same as today —
 * the keyboard simply offers no suggestion.
 */
const AUTOFILL_BY_ID: Readonly<Partial<Record<KycFieldId, FieldAutofill>>> = {
  LEGAL_FIRST_NAME: {
    textContentType: 'givenName',
    autoComplete: 'name-given',
    autoCapitalize: 'words',
  },
  LEGAL_MIDDLE_NAME: {
    textContentType: 'middleName',
    autoComplete: 'name-middle',
    autoCapitalize: 'words',
  },
  LEGAL_LAST_NAME: {
    textContentType: 'familyName',
    autoComplete: 'name-family',
    autoCapitalize: 'words',
  },
  RESIDENTIAL_ADDRESS_LINE_1: {
    textContentType: 'streetAddressLine1',
    autoComplete: 'address-line1',
    autoCapitalize: 'words',
  },
  RESIDENTIAL_ADDRESS_LINE_2: {
    textContentType: 'streetAddressLine2',
    autoComplete: 'address-line2',
    autoCapitalize: 'words',
  },
  RESIDENTIAL_ADDRESS_CITY: {
    textContentType: 'addressCity',
    autoComplete: 'postal-address-locality',
    autoCapitalize: 'words',
  },
  RESIDENTIAL_ADDRESS_REGION: {
    textContentType: 'addressState',
    autoComplete: 'postal-address-region',
    autoCapitalize: 'words',
  },
  RESIDENTIAL_ADDRESS_POSTAL_CODE: {
    textContentType: 'postalCode',
    autoComplete: 'postal-code',
    autoCapitalize: 'characters',
  },
  PHONE_NUMBER: {
    textContentType: 'telephoneNumber',
    autoComplete: 'tel',
    autoCapitalize: 'none',
  },
}

/** What the platform should offer for this field, if anything. */
export function fieldAutofill(field: FormField): FieldAutofill {
  return AUTOFILL_BY_ID[field.id] ?? {}
}
